// @ts-strict-ignore
import {
  Box,
  Button,
  ButtonVariant,
  Label,
  MultiSelect,
  TextArea,
  Text,
  useSnackBar,
  Modal,
} from '@usealloy/component-library';
import flatMap from 'lodash/flatMap';
import groupBy from 'lodash/groupBy';
import React, {
  useCallback, useContext, useState,
} from 'react';
import type { TFunction } from 'i18next';
import { useNavigate, useLocation } from 'react-router-dom';

import reverseTransformJsonToLegacyRepresentation from 'flow/store/engine/transformation/reverse';
import { ajaxFetch, typhonAjaxFetch } from '~/helpers/api';
import { routes, typhonRoutes } from '~/helpers/routes';
import { saveMatrixModels } from '~/flow/matrix-models/services';
import {
  selectInitialWorkflowData,
  selectInitialWorkflowProps,
  selectWorkflow,
  selectWorkflowData,
  selectWorkflowFailureHandlingMode,
  selectWorkflowIsSnippet,
  selectWorkflowProps,
} from '~/dashboard/views/workflows/store/redux/selectors';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import { UserContext } from '~/dashboard/context';
import { withI18n } from '~/helpers/i18n';

import { buildSaveToAWorkflowOptions } from './helpers';
import SaveToAWorkflowOption from './SaveToAWorkflowOption';
import type { SaveToAWorkflowOptionType } from './SaveToAWorkflowOption';
import { clearWorkflowSnapshots } from '~/dashboard/views/workflows/store/redux/slice';
import ChangelogCard from './ChangelogCard';
import generateVersionChangelog, { formatters } from '~/dashboard/views/workflows/service/changelog';
import { fillEmptyAxisAndMatrixValues, validateMatrixModels } from '~/dashboard/views/workflows/service/actions';
import { useCreateWorkflowVersion } from 'flow/api/useCreateWorkflowVersion';
import type { CreateWorkflowVersionPayload } from '~/helpers/api-new/modules/workflows/workflowsApi';
import { SPA_PREFIX } from '~/dashboard/constants';
import { isInSnippetEditor } from 'flow/editor/components/helpers';
import { filterPropsUsedByActions } from 'flow/service/snippets';

interface Props {
  onClose: () => void;
  isOpen: boolean;
  t: TFunction;
}


function SaveVersionModal(props: Props) {
  const {
    isOpen,
    onClose,
    t,
  } = props;

  const dispatch = useAppDispatch();
  const addSnackBar = useSnackBar();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    typhonDomain, featureFlags, agent, breadcrumbs,
  } = useContext(UserContext);
  const workflowProps = useAppSelector(selectWorkflowProps);
  const workflowData = useAppSelector(selectWorkflowData);
  const initialWorkflowData = useAppSelector(selectInitialWorkflowData);
  const initialWorkflowProps = useAppSelector(selectInitialWorkflowProps);
  const currentWorkflow = useAppSelector(selectWorkflow);
  const failureHandlingModeValue = useAppSelector(selectWorkflowFailureHandlingMode);
  const isSnippet = useAppSelector(selectWorkflowIsSnippet);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [newVersionNotes, setNewVersionNotes] = useState<string>('');
  const [
    selectedWorkflow, setSelectedWorkflow,
  ] = useState<SaveToAWorkflowOptionType>(null);

  const allWorkflows = workflowProps?.allWorkflows || [];
  const saveToAWorkflowOptions = buildSaveToAWorkflowOptions(currentWorkflow, allWorkflows);
  const createWorkflowVersionMutation = useCreateWorkflowVersion();

  const onChangeSaveToAWorkflow = useCallback((selected: SaveToAWorkflowOptionType) => {
    setSelectedWorkflow(selected);
  }, []);
  // TODO - BUILD-3998 - remove library FF as dependency
  const useTyphonWorkflowEndpoints = featureFlags.useTyphonWorkflowEndpoints
    || initialWorkflowProps?.workflow?.is_snippet;

  const computeChangelogAsMarkdown = useCallback(() => {
    try {
      const changelogResult = generateVersionChangelog({
        A: {
          props: initialWorkflowProps,
          version: initialWorkflowData,
        },
        B: {
          props: workflowProps,
          version: workflowData,
        },
        formatter: formatters.markdownFormatter,
        featureFlags,
      });
      return changelogResult.content;
    } catch (error) {
      return t('features.workflow.saveVersionModal.changelog.generatingError');
    }
  }, [initialWorkflowData, initialWorkflowProps, t, workflowData, workflowProps,
    featureFlags]);

  const buildRedirectUrl = (result, workflowToken) => {
    if (useTyphonWorkflowEndpoints) {
      const snippetContextUrl = `${SPA_PREFIX}/admin/library/global-repository/`;
      const isSnippetContext = location.pathname.includes(snippetContextUrl);
      return isSnippetContext ? `${snippetContextUrl}${workflowToken}/versions/${result.public_version}/update/` : `/workflows/${workflowToken}/versions/${result.public_version}/update/`;
    }
    return result.redirect_url.replace('applications/', 'workflows/');
  };
  // todo BUILD-898 - move most of this function to a mutation hook
  const onSaveVersion = async () => {
    setIsLoading(true);


    const targetApplicationToken = (selectedWorkflow?.value)
      ? selectedWorkflow.value : workflowData.token;

    let targetWorkflowLocked = false;
    if (featureFlags.workflowLocking) {
      try {
        const workflowCheckout = await typhonAjaxFetch(
          `${typhonDomain}${typhonRoutes.workflowCheckouts.workflowCheckout(targetApplicationToken)}`,
        );
        targetWorkflowLocked = workflowCheckout.checked_out;

        // If workflow is locked by a different user, throw error and do not save
        if (targetWorkflowLocked && workflowCheckout.checkout_agent?.agent_email !== agent.email) {
          onClose();
          addSnackBar({
            title: t('features.workflow.saveVersionModal.messages.errors.checkout'),
            type: 'danger',
          });
          return;
        }
      } catch (error) {
        addSnackBar({
          title: t('features.workflow.saveVersionModal.messages.errors.generic'),
          type: 'danger',
        });
        return;
      }
    }

    const {
      actions,
      actionOptions,
      thresholds,
      dataRoutes,
      matrixModels,
      matrixAxisData: matrixAxisDataRoutes,
      sdkConfig,
    } = reverseTransformJsonToLegacyRepresentation({
      actions: workflowData.actions,
      outcomes: workflowProps.outcomes,
      tags: workflowProps.tags,
      thresholds: workflowData.thresholds,
      dataRoutes: workflowData.dataRoutes,
      services: workflowProps.services,
      sdkConfig: workflowData.sdkConfig,
    });

    let finalActionOptions = actionOptions;

    if (matrixModels?.length) {
      const matrixModelsWithValues = matrixModels.map(fillEmptyAxisAndMatrixValues);

      const matrixModelsValidationErrors = validateMatrixModels({
        actions, matrixModels: matrixModelsWithValues, matrixAxisDataRoutes,
      });

      if (matrixModelsValidationErrors.length) {
        // Don't save if the matrix models are invalid
        // and addSnackbar for each error
        matrixModelsValidationErrors.forEach(error => {
          addSnackBar({ title: error, type: 'danger' });
        });
        onClose();
        return;
      }
      // only save matrix models separately if using daffy save
      if (!useTyphonWorkflowEndpoints && matrixModelsWithValues.length > 0) {
        const {
          updatedModels,
          createdModels,
        } = await saveMatrixModels(typhonDomain, matrixModelsWithValues, true);

        const groupedActionOptionsByActionId = groupBy(actionOptions, 'actionId');

        [...updatedModels, ...createdModels].forEach(
          ({ matrix_model_token: token, version, actionId }) => {
            groupedActionOptionsByActionId[actionId].forEach(actionOption => {
              if (actionOption.key === 'matrix_model_token') {
                actionOption.value = token;
              }

              if (actionOption.key === 'matrix_model_public_version') {
                actionOption.value = version;
              }

              return actionOption;
            });
          },
        );
        finalActionOptions = flatMap(groupedActionOptionsByActionId);
      }
    }

    const newVersionCreateUrl = routes.applications.versions.create(workflowData.token);

    try {
      let result;
      if (useTyphonWorkflowEndpoints) {
        let snippetVersionPayload: CreateWorkflowVersionPayload['snippetVersion'];
        if (isInSnippetEditor(breadcrumbs)) {
          const filteredProps = filterPropsUsedByActions({
            actions: workflowData.actions,
            thresholds: workflowData.thresholds,
            workflowProps,
          });

          snippetVersionPayload = {
            props: {
              tags: filteredProps.tags,
              outcomes: filteredProps.outcomes,
              services: filteredProps.services,
              publishedAttributes: filteredProps.publishedAttributes,
              reasonCodeGroups: filteredProps.reasonCodeGroups,
            },
          };
        }

        const payload: CreateWorkflowVersionPayload = {
          workflowVersion: {
            name: workflowData.name!,
            token: targetApplicationToken!,
            workflowType: workflowProps.workflow.type,
            actions: workflowData.actions,
            thresholds: workflowData.thresholds,
            dataRoutes: workflowData.dataRoutes,
            failureHandlingMode: failureHandlingModeValue,
            sdkConfig: workflowData.sdkConfig,
          },
          workflowVersionMetadata: {
            changes: computeChangelogAsMarkdown(),
            notes: newVersionNotes,
            parentPublicVersion: workflowData.publicVersion ?? null,
          },
          ...(snippetVersionPayload && { snippetVersion: snippetVersionPayload }),
        };

        result = await createWorkflowVersionMutation.mutateAsync(payload);
      } else {
        result = await ajaxFetch(newVersionCreateUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            actions,
            action_options: finalActionOptions,
            thresholds, // @todo: engine needs to clean up unapplied optional mla values
            data_routes: dataRoutes,
            relationships: {}, // @todo: engine needs to support action relationships
            version_notes: newVersionNotes,
            version_changes: computeChangelogAsMarkdown(),
            parent_version: workflowData.publicVersion ? String(workflowData.publicVersion) : null,
            application_token: targetApplicationToken,
            sdk_config: sdkConfig,
            failure_handling_mode: failureHandlingModeValue,
          }),
        });
      }

      if (result.message === 'success') {
        onClose();
        addSnackBar({
          duration: 1000,
          title: t('features.workflow.saveVersionModal.messages.success.save'),
          type: 'success',
        });

        // If target workflow is locked, unlock it
        if (featureFlags.workflowLocking && targetWorkflowLocked) {
          try {
            await typhonAjaxFetch(
              `${typhonDomain}${typhonRoutes.workflowCheckouts.postCheckin(targetApplicationToken)}`,
              {
                method: 'POST',
                body: JSON.stringify({ workflow_public_version: result.public_version }),
                silent: true,
              },
            );
          } catch (_err) {
            addSnackBar({
              duration: 1000,
              title: t('features.workflow.saveVersionModal.messages.errors.checkin'),
              type: 'danger',
            });
          }
        }
        /**
         * the snapshots are cleared before reloading to normalize the 'unsaved changes' comparison
         * todo BUILD-898 - this won't be necessary after supporting saving a version without a
         * full page refresh
         */
        dispatch(clearWorkflowSnapshots());
        const redirectUrl = buildRedirectUrl(result, targetApplicationToken);
        navigate(redirectUrl);
        navigate(0);
      } else {
        addSnackBar({
          title: t('features.workflow.saveVersionModal.messages.errors.generic'),
          type: 'danger',
        });
      }
    } catch (error) {
      addSnackBar({
        title: t('features.workflow.saveVersionModal.messages.errors.generic'),
        type: 'danger',
      });
    }

    setIsLoading(false);
  };


  return (
    <Modal
      header={t('features.workflow.saveVersionModal.header') as unknown as React.ReactNode}
      id="save-workflow-version-modal"
      isOpen={isOpen}
      onClose={onClose}
      primaryAction={(
        <Button
          variant={ButtonVariant.primary}
          onClick={onSaveVersion}
          disabled={isLoading}
          loading={isLoading}
          data-testid="save-version-button"
        >
          Save
        </Button>
      )}
      secondaryAction={(
        <Button
          variant={ButtonVariant.secondary}
          onClick={onClose}
        >
          Cancel
        </Button>
      )}
    >
      <Box direction="column" gap={5}>
        <TextArea
          placeholder={t('features.workflow.saveVersionModal.versionNotes.placeholder')}
          label={t('features.workflow.saveVersionModal.labels.versionNotes') as unknown as React.ReactNode}
          onChange={event => {
            setNewVersionNotes(event.target.value);
          }}
        />

        <ChangelogCard />

        {!isSnippet && (
          <Box justifyContent="flex-start" direction="column" gap={2}>
            <Box justifyContent="flex-start" direction="row" gap={2}>
              <Label htmlFor="save_to_workflow">
                Save to another Workflow version
              </Label>
              <Text style={{ color: 'var(--cl-gray-600)' }}>
                Optional
              </Text>
            </Box>
            <MultiSelect
              components={{
                Option: SaveToAWorkflowOption,
              }}
              id="save_to_workflow"
              isMulti={false}
              isSearchable
              onChange={onChangeSaveToAWorkflow}
              options={saveToAWorkflowOptions}
              placeholder="Select"
              selectedOptions={selectedWorkflow}
            />
          </Box>
        )}
      </Box>
    </Modal>
  );
}

export default withI18n({
  importPath: 'features/workflow',
  translationKey: 'features.workflow.saveVersionModal',
})(SaveVersionModal);
